<template>
  <!-- Privacy / blurred mode: plain avatar, no interaction -->
  <div v-if="isBlurred" :class="['flex-shrink-0 overflow-hidden bg-gray-200 flex items-center justify-center', roundedClass, sizeClass]">
    <img v-if="avatarSrc" :src="avatarSrc" :class="['object-cover blur-md select-none', sizeClass]" :alt="name" />
    <UserRound v-else class="h-6 w-6 text-gray-400 blur-md select-none" />
  </div>

  <!-- Normal mode: hover-scale avatar with navigation dropdown -->
  <DropdownMenuRoot v-else>
    <DropdownMenuTrigger as-child @click.stop>
      <button
        :class="[
          'flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-cosmicPurple/40 transition-transform duration-150 hover:scale-110',
          roundedClass
        ]"
      >
        <AvatarImage :src="avatarSrc" :alt="name" :size="size" :rounded="rounded" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuPortal>
      <DropdownMenuContent
        class="z-50 min-w-[180px] overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        :side-offset="6"
        align="start"
        @click.stop
      >
        <DropdownMenuItem
          class="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer select-none outline-none hover:bg-gray-100 focus:bg-gray-100"
          @select="openPublicProfile"
        >
          <ExternalLink class="w-3.5 h-3.5 text-gray-400" />
          Community profile
        </DropdownMenuItem>
        <DropdownMenuItem
          class="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer select-none outline-none hover:bg-gray-100 focus:bg-gray-100"
          @select="openManagementProfile"
        >
          <UserCog class="w-3.5 h-3.5 text-gray-400" />
          Management profile
        </DropdownMenuItem>
        <DropdownMenuSeparator class="my-1 h-px bg-gray-100" />
        <DropdownMenuItem
          class="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer select-none outline-none hover:bg-gray-100 focus:bg-gray-100"
          @select="openCurriculum"
        >
          <BookOpen class="w-3.5 h-3.5 text-gray-400" />
          Curriculum
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import { ExternalLink, UserCog, BookOpen, UserRound } from 'lucide-vue-next'
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuPortal,
} from 'reka-ui'
import AvatarImage from '@/components/common/AvatarImage.vue'

const props = withDefaults(
  defineProps<{
    userId: number
    username: string
    avatarSrc?: string | null
    name: string
    curriculumPath: string
    isBlurred?: boolean
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
    rounded?: 'full' | 'lg'
  }>(),
  {
    isBlurred: false,
    size: 'lg',
    rounded: 'full',
  },
)

const sizeClass = {
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
  lg: 'h-10 w-10',
  xl: 'h-12 w-12',
  '2xl': 'h-16 w-16',
}[props.size]

const roundedClass = props.rounded === 'lg' ? 'rounded-lg' : 'rounded-full'

const router = useRouter()

function openPublicProfile() {
  window.open(`https://outtalent.com/profile/${props.username}`, '_blank')
}

function openManagementProfile() {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push(`/manage/fellow/${props.userId}`)
}

function openCurriculum() {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push(props.curriculumPath)
}
</script>
